import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { defaultFilter } from 'cmdk';

import {
    CommandDialog,
    Command,
    CommandInput,
    CommandList,
    CommandGroup,
    CommandItem,
    CommandEmpty,
} from '@/components/ui/command';
import { PAGES } from 'constants/pages';

// Pages with a navSection are the navigable destinations surfaced in the palette.
const palettePages = Object.values(PAGES)
    .filter((page) => page.navSection !== undefined)
    .sort((a, b) => (a.shortLabel ?? a.label).localeCompare(b.shortLabel ?? b.label, 'fr'));

const normalize = (s: string): string =>
    s
        .toLowerCase()
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '');

// Tiered scoring so exact/prefix matches outrank fuzzy ones (e.g. "sites" → "Sites" first).
function scorePage(value: string, search: string, keywords?: string[]): number {
    const q = normalize(search.trim());
    if (!q) return 1;

    let best = 0;
    for (const candidate of [value, ...(keywords ?? [])]) {
        const v = normalize(candidate);
        if (v === q) best = Math.max(best, 1);
        else if (v.startsWith(q)) best = Math.max(best, 0.9);
        else if (v.split(' ').some((word) => word.startsWith(q))) best = Math.max(best, 0.7);
        else if (v.includes(q)) best = Math.max(best, 0.5);
    }
    if (best > 0) return best;

    // Fall back to cmdk's fuzzy scorer, de-prioritised below any direct match above.
    return (defaultFilter?.(value, search, keywords) ?? 0) * 0.3;
}

const isInputFocused = (): boolean => {
    const active = document.activeElement;
    if (!active) return false;
    const tag = active.tagName.toLowerCase();
    return tag === 'input' || tag === 'textarea' || tag === 'select' || (active as HTMLElement).isContentEditable;
};

export function CommandPalette() {
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const pendingKeyRef = useRef<string | null>(null);

    const handleSelect = useCallback(
        (url: string) => {
            setOpen(false);
            navigate(url);
        },
        [navigate],
    );

    // Cmd/Ctrl+K shortcut + auto-open on Home when typing
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            // Cmd/Ctrl+K toggle
            if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                setOpen((prev) => !prev);
                return;
            }

            // Auto-open on Home page when user starts typing
            const isHome = location.pathname === '/' || location.pathname === '/home';
            if (!isHome) return;
            if (isInputFocused()) return;
            if (e.metaKey || e.ctrlKey || e.altKey) return;
            if (e.key.length !== 1) return; // ignore special keys

            pendingKeyRef.current = e.key;
            setOpen(true);
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [location.pathname]);

    // Forward pending key to cmdk input after dialog opens
    useEffect(() => {
        if (!open || !pendingKeyRef.current) return;

        // Small delay to let the dialog render and focus the input
        const timer = setTimeout(() => {
            const input = document.querySelector<HTMLInputElement>('[data-slot="command-input"]');
            if (input && pendingKeyRef.current) {
                const nativeSetter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set;
                if (nativeSetter) {
                    nativeSetter.call(input, pendingKeyRef.current);
                    input.dispatchEvent(new Event('input', { bubbles: true }));
                }
            }
            pendingKeyRef.current = null;
        }, 10);

        return () => clearTimeout(timer);
    }, [open]);

    return (
        <CommandDialog open={open} onOpenChange={setOpen} title="Recherche rapide" description="Naviguer vers une page">
            <Command filter={scorePage}>
                <CommandInput placeholder="Rechercher une page..." />
                <CommandList>
                    <CommandEmpty>Aucun résultat.</CommandEmpty>
                    <CommandGroup>
                        {palettePages.map((page) => (
                            <CommandItem
                                key={page.url}
                                value={page.label}
                                keywords={
                                    page.shortLabel && page.shortLabel !== page.label ? [page.shortLabel] : undefined
                                }
                                onSelect={() => handleSelect(page.url)}
                            >
                                {page.icon}
                                <span>{page.shortLabel ?? page.label}</span>
                            </CommandItem>
                        ))}
                    </CommandGroup>
                </CommandList>
            </Command>
        </CommandDialog>
    );
}
